import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";

import { useStyle } from "../../context/styleContext/styleContext";
import Seo from "../../components/Seo";

const emptyImage = {
  URI: "",
  altText: "",
};

const emptyControl = {
  name: "",
  description: "",
};

const emptyForm = {
  name: "",
  SKU: "",
  cardSummary: "",
  cardCallout: "",
  shortDescription: "",
  longDescription: "",
  bulletPoint1: "",
  bulletPoint2: "",
  bulletPoint3: "",
  bulletPoint4: "",
  bulletPoint5: "",
  msrp: "",
  price: "",
  images: [],
  audioURIs: "",
  controls: [],
  UPC: "",
  productURI: "",
  productVersion: "",
  windowsDownloadURI: "",
  macDownloadURI: "",
  versionDownloads: "",
  googleMarketplaceImage: "",
  visibleOnWebsite: true,
  starRating: "",
  ratingCount: "",
};

export default function AdminProductPage() {
  const { style } = useStyle();
  const { productId } = useParams();
  const navigate = useNavigate();

  const token = useMemo(() => localStorage.getItem("token") || "", []);

  const [formData, setFormData] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const panelBg = style.darkPanel || "rgba(9, 12, 18, 0.45)";

  const sharedTextFieldSx = {
    width: "100%",
    "& .MuiInputBase-root": {
      color: style.color4,
      backgroundColor: style.color2,
      borderRadius: "8px",
    },
    "& .MuiOutlinedInput-root fieldset": {
      borderColor: style.color2,
    },
    "& .MuiOutlinedInput-root:hover fieldset": {
      borderColor: style.color3,
    },
    "& .MuiOutlinedInput-root.Mui-focused fieldset": {
      borderColor: style.accentGreen,
    },
    "& .MuiInputLabel-root": {
      color: style.color3,
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: style.color4,
    },
    "& .MuiFormHelperText-root": {
      color: style.color3,
      marginLeft: 0,
    },
  };

  const sectionCardSx = {
    p: { xs: 1.5, sm: 2 },
    borderRadius: "14px",
    backgroundColor: panelBg,
    border: "1px solid rgba(255,255,255,0.08)",
  };

  const arrayToText = (value) => {
    if (!Array.isArray(value)) return "";
    return value.filter(Boolean).join("\n");
  };

  const textToArray = (value) => {
    if (!value || typeof value !== "string") return [];
    return value
      .split("\n")
      .map((item) => item.trim())
      .filter(Boolean);
  };

  const safeNumber = (value) => {
    if (value === "" || value === null || value === undefined) return "";
    const number = Number(value);
    return Number.isNaN(number) ? "" : number;
  };

  const normalizeImages = (images) => {
    if (!Array.isArray(images)) return [];

    return images
      .filter((image) => image && typeof image === "object")
      .map((image) => ({
        URI: image.URI || image.uri || image.url || "",
        altText: image.altText || image.alt || "",
      }));
  };

  const normalizeControls = (controls) => {
    if (!Array.isArray(controls)) return [];

    return controls
      .filter((control) => control && typeof control === "object")
      .map((control) => ({
        name: control.name || "",
        description: control.description || "",
      }));
  };

  const productToFormData = (product) => ({
    name: product.name || "",
    SKU: product.SKU || "",
    cardSummary: product.cardSummary || "",
    cardCallout: product.cardCallout || "",
    shortDescription: product.shortDescription || "",
    longDescription: product.longDescription || "",
    bulletPoint1: product.bulletPoint1 || "",
    bulletPoint2: product.bulletPoint2 || "",
    bulletPoint3: product.bulletPoint3 || "",
    bulletPoint4: product.bulletPoint4 || "",
    bulletPoint5: product.bulletPoint5 || "",
    msrp: product.msrp ?? "",
    price: product.price ?? "",
    images: normalizeImages(product.images),
    audioURIs: arrayToText(product.audioURIs),
    controls: normalizeControls(product.controls),
    UPC: product.UPC || "",
    productURI: product.productURI || "",
    productVersion: product.productVersion || "",
    windowsDownloadURI: product.windowsDownloadURI || "",
    macDownloadURI: product.macDownloadURI || "",
    versionDownloads: arrayToText(product.versionDownloads),
    googleMarketplaceImage: product.googleMarketplaceImage || "",
    visibleOnWebsite: Boolean(product.visibleOnWebsite),
    starRating: product.starRating ?? "",
    ratingCount: product.ratingCount ?? "",
  });

  const loadProduct = async () => {
    try {
      setLoading(true);

      const { data } = await axios.post("/api/get-all-products");

      if (data?.error) {
        toast.error(data.error);
        return;
      }

      const products = Array.isArray(data) ? data : data.products || [];
      const product = products.find((item) => item._id === productId);

      if (!product) {
        toast.error("Product not found.");
        navigate("/admin/products");
        return;
      }

      setFormData(productToFormData(product));
    } catch (error) {
      toast.error(error?.response?.data?.error || "Unable to load product.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
    loadProduct();
  }, [productId]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const buildProductPayload = () => {
    const cleanedImages = formData.images
      .map((image) => ({
        URI: image.URI?.trim() || "",
        altText: image.altText?.trim() || "",
      }))
      .filter((image) => image.URI || image.altText);

    const imageURIs = cleanedImages.map((image) => image.URI).filter(Boolean);

    const cleanedControls = formData.controls
      .map((control) => ({
        name: control.name?.trim() || "",
        description: control.description?.trim() || "",
      }))
      .filter((control) => control.name || control.description);

    return {
      name: formData.name,
      SKU: formData.SKU,
      cardSummary: formData.cardSummary,
      cardCallout: formData.cardCallout,
      shortDescription: formData.shortDescription,
      longDescription: formData.longDescription,
      bulletPoint1: formData.bulletPoint1,
      bulletPoint2: formData.bulletPoint2,
      bulletPoint3: formData.bulletPoint3,
      bulletPoint4: formData.bulletPoint4,
      bulletPoint5: formData.bulletPoint5,
      msrp: safeNumber(formData.msrp),
      price: safeNumber(formData.price),
      images: cleanedImages,
      imageURIs,
      audioURIs: textToArray(formData.audioURIs),
      controls: cleanedControls,
      UPC: formData.UPC,
      productURI: formData.productURI,
      productVersion: formData.productVersion,
      windowsDownloadURI: formData.windowsDownloadURI,
      macDownloadURI: formData.macDownloadURI,
      versionDownloads: textToArray(formData.versionDownloads),
      googleMarketplaceImage: formData.googleMarketplaceImage,
      visibleOnWebsite: Boolean(formData.visibleOnWebsite),
      starRating: safeNumber(formData.starRating),
      ratingCount: safeNumber(formData.ratingCount),
    };
  };

  const handleSave = async () => {
    try {
      if (!token) {
        toast.error("You must be logged in as an admin.");
        return;
      }

      setSaving(true);

      const { data } = await axios.post("/api/update-product", {
        token,
        productId,
        productData: buildProductPayload(),
      });

      if (data?.error) {
        toast.error(data.error);
        return;
      }

      toast.success("Product updated.");
    } catch (error) {
      toast.error(error?.response?.data?.error || "Unable to save product.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this product?"
    );

    if (!confirmed) return;

    try {
      if (!token) {
        toast.error("You must be logged in as an admin.");
        return;
      }

      const { data } = await axios.post("/api/delete-product", {
        token,
        productId,
      });

      if (data?.error) {
        toast.error(data.error);
        return;
      }

      toast.success("Product deleted.");
      navigate("/admin/products");
    } catch (error) {
      toast.error(error?.response?.data?.error || "Unable to delete product.");
    }
  };

  const addImage = () => {
    setFormData((prev) => ({
      ...prev,
      images: [...prev.images, { ...emptyImage }],
    }));
  };

  const updateImage = (index, field, value) => {
    setFormData((prev) => {
      const images = [...prev.images];
      images[index] = {
        ...images[index],
        [field]: value,
      };

      return {
        ...prev,
        images,
      };
    });
  };

  const removeImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, imageIndex) => imageIndex !== index),
    }));
  };

  const addControl = () => {
    setFormData((prev) => ({
      ...prev,
      controls: [...prev.controls, { ...emptyControl }],
    }));
  };

  const updateControl = (index, field, value) => {
    setFormData((prev) => {
      const controls = [...prev.controls];
      controls[index] = {
        ...controls[index],
        [field]: value,
      };

      return {
        ...prev,
        controls,
      };
    });
  };

  const removeControl = (index) => {
    setFormData((prev) => ({
      ...prev,
      controls: prev.controls.filter(
        (_, controlIndex) => controlIndex !== index
      ),
    }));
  };

  const mainPreviewImage =
    formData.images.find((image) => image?.URI)?.URI ||
    formData.googleMarketplaceImage ||
    "";

  const mainPreviewAlt =
    formData.images.find((image) => image?.URI)?.altText ||
    formData.name ||
    "Product image preview";

  return (
    <>
      <Seo
        title={`${formData.name || "Product"} | Admin | Parish Audio`}
        description="Edit Parish Audio product."
        canonical={`https://parishaudio.com/admin/products/${productId}`}
        robots="noindex,nofollow"
      />

      <Box
        sx={{
          backgroundColor: "black",
          minHeight: "100vh",
          color: style.color4,
          px: { xs: 1.25, sm: 1.5, md: 2.5 },
          py: { xs: 2, md: 3 },
        }}
      >
        <Box sx={{ maxWidth: 1350, mx: "auto" }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: "20px",
              overflow: "hidden",
              backgroundColor: style.color1,
              border: `1px solid ${style.color2}`,
            }}
          >
            <Box
              sx={{
                px: { xs: 1.5, sm: 2, md: 3 },
                py: { xs: 2.25, md: 3 },
                backgroundColor: panelBg,
                borderBottom: `1px solid ${style.color2}`,
              }}
            >
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.5}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", md: "center" }}
              >
                <Stack spacing={1} alignItems="flex-start">
                <a href="/admin/products">
                  <Chip
                    icon={<Inventory2RoundedIcon />}
                    label="Admin Product"
                    sx={{
                      borderRadius: "5px",
                      fontWeight: 700,
                      color: style.color4,
                      backgroundColor: "rgba(255,255,255,0.06)",
                    }}
                  />
</a>
                  <Typography
                    sx={{
                      color: style.color4,
                      fontSize: { xs: "1.9rem", md: "2.5rem" },
                      fontWeight: 800,
                      lineHeight: 1.05,
                      letterSpacing: "-0.03em",
                    }}
                  >
                    {loading ? "Loading Product..." : formData.name || "Product"}
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.65 }}>
                    Edit product details, images, controls, downloads, and
                    storefront visibility.
                  </Typography>
                </Stack>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <Button
                    startIcon={<ArrowBackRoundedIcon />}
                    onClick={() => navigate("/admin/products")}
                    sx={{
                      borderRadius: "5px",
                      textTransform: "none",
                      fontWeight: 700,
                      color: style.color4,
                      backgroundColor: "rgba(255,255,255,0.05)",
                      border: "1px solid rgba(255,255,255,0.08)",
                    }}
                  >
                    Back
                  </Button>

                  <Button
                    startIcon={<SaveRoundedIcon />}
                    onClick={handleSave}
                    disabled={saving || loading}
                    sx={{
                      borderRadius: "5px",
                      textTransform: "none",
                      fontWeight: 700,
                      color: style.color4,
                      backgroundColor: style.accentGreen,
                    }}
                  >
                    {saving ? "Saving..." : "Save"}
                  </Button>

                  <Button
                    startIcon={<DeleteRoundedIcon />}
                    onClick={handleDelete}
                    disabled={loading}
                    sx={{
                      borderRadius: "5px",
                      textTransform: "none",
                      fontWeight: 700,
                      color: "#ffb4b4",
                      backgroundColor: "rgba(255,255,255,0.03)",
                      border: "1px solid rgba(255,255,255,0.08)",
                    }}
                  >
                    Delete
                  </Button>
                </Stack>
              </Stack>
            </Box>

            <Box sx={{ px: { xs: 1.5, sm: 2, md: 3 }, py: 2.5 }}>
              <Stack spacing={2}>
                <Paper elevation={0} sx={sectionCardSx}>
                  <Stack spacing={1.5}>
                    <Typography sx={{ color: style.color4, fontWeight: 800 }}>
                      Product Details
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Name"
                          value={formData.name}
                          onChange={(e) => handleChange("name", e.target.value)}
                          size="small"
                          sx={sharedTextFieldSx}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          label="SKU"
                          value={formData.SKU}
                          onChange={(e) => handleChange("SKU", e.target.value)}
                          size="small"
                          sx={sharedTextFieldSx}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          label="Product Version"
                          value={formData.productVersion}
                          onChange={(e) =>
                            handleChange("productVersion", e.target.value)
                          }
                          size="small"
                          sx={sharedTextFieldSx}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          label="UPC"
                          value={formData.UPC}
                          onChange={(e) => handleChange("UPC", e.target.value)}
                          size="small"
                          sx={sharedTextFieldSx}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          label="MSRP"
                          type="number"
                          value={formData.msrp}
                          onChange={(e) => handleChange("msrp", e.target.value)}
                          size="small"
                          sx={sharedTextFieldSx}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          label="Price"
                          type="number"
                          value={formData.price}
                          onChange={(e) => handleChange("price", e.target.value)}
                          size="small"
                          sx={sharedTextFieldSx}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          label="Star Rating"
                          type="number"
                          value={formData.starRating}
                          onChange={(e) =>
                            handleChange("starRating", e.target.value)
                          }
                          size="small"
                          sx={sharedTextFieldSx}
                        />
                      </Grid>

                      <Grid item xs={12} md={8}>
                        <TextField
                          label="Product URI"
                          value={formData.productURI}
                          onChange={(e) =>
                            handleChange("productURI", e.target.value)
                          }
                          size="small"
                          sx={sharedTextFieldSx}
                        />
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.visibleOnWebsite}
                              onChange={(e) =>
                                handleChange(
                                  "visibleOnWebsite",
                                  e.target.checked
                                )
                              }
                              sx={{
                                color: style.color3,
                                "&.Mui-checked": {
                                  color: style.accentGreen,
                                },
                              }}
                            />
                          }
                          label="Visible on website"
                          sx={{ color: style.color4 }}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          label="Card Summary"
                          value={formData.cardSummary}
                          onChange={(e) =>
                            handleChange("cardSummary", e.target.value)
                          }
                          multiline
                          minRows={2}
                          size="small"
                          sx={sharedTextFieldSx}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          label="Card Callout"
                          value={formData.cardCallout}
                          onChange={(e) =>
                            handleChange("cardCallout", e.target.value)
                          }
                          multiline
                          minRows={2}
                          size="small"
                          sx={sharedTextFieldSx}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          label="Short Description"
                          value={formData.shortDescription}
                          onChange={(e) =>
                            handleChange("shortDescription", e.target.value)
                          }
                          multiline
                          minRows={2}
                          size="small"
                          sx={sharedTextFieldSx}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          label="Long Description"
                          value={formData.longDescription}
                          onChange={(e) =>
                            handleChange("longDescription", e.target.value)
                          }
                          multiline
                          minRows={6}
                          size="small"
                          sx={sharedTextFieldSx}
                        />
                      </Grid>

                      {[1, 2, 3, 4, 5].map((number) => (
                        <Grid
                          item
                          xs={12}
                          md={number <= 3 ? 4 : 6}
                          key={number}
                        >
                          <TextField
                            label={`Bullet Point ${number}`}
                            value={formData[`bulletPoint${number}`]}
                            onChange={(e) =>
                              handleChange(
                                `bulletPoint${number}`,
                                e.target.value
                              )
                            }
                            size="small"
                            sx={sharedTextFieldSx}
                          />
                        </Grid>
                      ))}

                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Windows Download URI"
                          value={formData.windowsDownloadURI}
                          onChange={(e) =>
                            handleChange("windowsDownloadURI", e.target.value)
                          }
                          size="small"
                          sx={sharedTextFieldSx}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Mac Download URI"
                          value={formData.macDownloadURI}
                          onChange={(e) =>
                            handleChange("macDownloadURI", e.target.value)
                          }
                          size="small"
                          sx={sharedTextFieldSx}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          label="Version Downloads"
                          value={formData.versionDownloads}
                          onChange={(e) =>
                            handleChange("versionDownloads", e.target.value)
                          }
                          multiline
                          minRows={3}
                          size="small"
                          sx={sharedTextFieldSx}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          label="Audio URIs"
                          value={formData.audioURIs}
                          onChange={(e) =>
                            handleChange("audioURIs", e.target.value)
                          }
                          multiline
                          minRows={3}
                          size="small"
                          sx={sharedTextFieldSx}
                        />
                      </Grid>
                    </Grid>
                  </Stack>
                </Paper>

                <Paper elevation={0} sx={sectionCardSx}>
                  <Stack spacing={1.5}>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      justifyContent="space-between"
                      spacing={1}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <ImageRoundedIcon sx={{ color: style.color3 }} />
                        <Typography sx={{ color: style.color4, fontWeight: 800 }}>
                          Product Images
                        </Typography>
                      </Stack>

                      <Button
                        startIcon={<AddRoundedIcon />}
                        onClick={addImage}
                        sx={{
                          borderRadius: "5px",
                          textTransform: "none",
                          fontWeight: 700,
                          color: style.color4,
                          backgroundColor: "rgba(255,255,255,0.05)",
                          border: "1px solid rgba(255,255,255,0.08)",
                        }}
                      >
                        Add Image
                      </Button>
                    </Stack>

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <Box
                          sx={{
                            height: 190,
                            borderRadius: "10px",
                            border: "1px solid rgba(255,255,255,0.1)",
                            backgroundColor: "rgba(255,255,255,0.04)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            overflow: "hidden",
                            p: 1,
                          }}
                        >
                          {mainPreviewImage ? (
                            <Box
                              component="img"
                              src={mainPreviewImage}
                              alt={mainPreviewAlt}
                              sx={{
                                maxWidth: "100%",
                                maxHeight: "100%",
                                objectFit: "contain",
                              }}
                            />
                          ) : (
                            <Typography sx={{ color: style.color3 }}>
                              No preview
                            </Typography>
                          )}
                        </Box>
                      </Grid>

                      <Grid item xs={12} md={8}>
                        <TextField
                          label="Google Marketplace Image"
                          value={formData.googleMarketplaceImage}
                          onChange={(e) =>
                            handleChange(
                              "googleMarketplaceImage",
                              e.target.value
                            )
                          }
                          size="small"
                          sx={sharedTextFieldSx}
                        />
                      </Grid>
                    </Grid>

                    {formData.images.map((image, index) => (
                      <Paper
                        key={index}
                        elevation={0}
                        sx={{
                          p: 1.5,
                          borderRadius: "10px",
                          backgroundColor: "rgba(255,255,255,0.03)",
                          border: "1px solid rgba(255,255,255,0.08)",
                        }}
                      >
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={5}>
                            <TextField
                              label={`Image ${index + 1} URI`}
                              value={image.URI}
                              onChange={(e) =>
                                updateImage(index, "URI", e.target.value)
                              }
                              size="small"
                              sx={sharedTextFieldSx}
                            />
                          </Grid>

                          <Grid item xs={12} md={7}>
                            <TextField
                              label={`Image ${index + 1} Alt Text`}
                              value={image.altText}
                              onChange={(e) =>
                                updateImage(index, "altText", e.target.value)
                              }
                              multiline
                              minRows={3}
                              size="small"
                              sx={sharedTextFieldSx}
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <Button
                              startIcon={<DeleteRoundedIcon />}
                              onClick={() => removeImage(index)}
                              sx={{
                                borderRadius: "5px",
                                textTransform: "none",
                                fontWeight: 700,
                                color: "#ffb4b4",
                                backgroundColor: "rgba(255,255,255,0.03)",
                                border: "1px solid rgba(255,255,255,0.08)",
                              }}
                            >
                              Remove Image
                            </Button>
                          </Grid>
                        </Grid>
                      </Paper>
                    ))}
                  </Stack>
                </Paper>

                <Paper elevation={0} sx={sectionCardSx}>
                  <Stack spacing={1.5}>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      justifyContent="space-between"
                      spacing={1}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <TuneRoundedIcon sx={{ color: style.color3 }} />
                        <Typography sx={{ color: style.color4, fontWeight: 800 }}>
                          Controls
                        </Typography>
                      </Stack>

                      <Button
                        startIcon={<AddRoundedIcon />}
                        onClick={addControl}
                        sx={{
                          borderRadius: "5px",
                          textTransform: "none",
                          fontWeight: 700,
                          color: style.color4,
                          backgroundColor: "rgba(255,255,255,0.05)",
                          border: "1px solid rgba(255,255,255,0.08)",
                        }}
                      >
                        Add Control
                      </Button>
                    </Stack>

                    {formData.controls.map((control, index) => (
                      <Paper
                        key={index}
                        elevation={0}
                        sx={{
                          p: 1.5,
                          borderRadius: "10px",
                          backgroundColor: "rgba(255,255,255,0.03)",
                          border: "1px solid rgba(255,255,255,0.08)",
                        }}
                      >
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={4}>
                            <TextField
                              label="Control Name"
                              value={control.name}
                              onChange={(e) =>
                                updateControl(index, "name", e.target.value)
                              }
                              size="small"
                              sx={sharedTextFieldSx}
                            />
                          </Grid>

                          <Grid item xs={12} md={8}>
                            <TextField
                              label="Control Description"
                              value={control.description}
                              onChange={(e) =>
                                updateControl(
                                  index,
                                  "description",
                                  e.target.value
                                )
                              }
                              multiline
                              minRows={3}
                              size="small"
                              sx={sharedTextFieldSx}
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <Button
                              startIcon={<DeleteRoundedIcon />}
                              onClick={() => removeControl(index)}
                              sx={{
                                borderRadius: "5px",
                                textTransform: "none",
                                fontWeight: 700,
                                color: "#ffb4b4",
                                backgroundColor: "rgba(255,255,255,0.03)",
                                border: "1px solid rgba(255,255,255,0.08)",
                              }}
                            >
                              Remove Control
                            </Button>
                          </Grid>
                        </Grid>
                      </Paper>
                    ))}
                  </Stack>
                </Paper>
              </Stack>
            </Box>
          </Paper>
        </Box>
      </Box>
    </>
  );
}